import { Task, Update, defModule, type TaskDeclaration, type TaskMessage } from '@st/redux'
import {
  DocumentGenerationError,
  DocumentWarning,
  SDKRequestFor,
  STDocument,
  STDocumentType,
  STFolderDownloadState
} from '@st/sdk'
import type { ArchiveGenerationError, FileReadError } from '@st/util/archive/archive-errors'
import { Effect, pipe } from 'effect'
import { reportHandledError } from '~/features/error-reporting/report-handled-error'
import { runSdkRequest } from '~/features/module-runtime'
import {
  BrowserDownloadFetchError,
  BrowserDownloads,
  SdkClient,
  Snackbar,
  type SdkFailure
} from '~/effects'
import { setToggle } from '@st/util/array-set'
import { isNotEmpty } from '@st/util/json-value'
import { direction, sort, SortState, SortTransitions, toggleSort } from '@st/util/sort'
import { match, P } from '@st/pattern'
import { zipEntries } from '@st/util/archive/zip-entries'
import { browserFileEntry } from '@st/util/archive/browser-file'
import { sanitizeFilename, extname, rootname } from '@st/util/file'
import { parseISO8601Time } from '@st/util/time'
import { getDocumentSections } from './st-folder-selectors'
import { formatFolderEntityName } from './st-folder-display'

type WorkpaperDestination = NonNullable<SDKRequestFor<'folders/generateWorkpaper'>['destination']>

export type STDownloadFolderModuleState =
  | { status: 'idle' }
  | { status: 'loading'; folderId: string }
  | { status: 'loadFailed'; folderId: string }
  | {
      status: 'loaded'
      folderId: string
      downloadState: STFolderDownloadState
      selectedDocumentIds: string[]
      download: Download
      sortState: SortState<DownloadListSortColumn> | undefined
      refreshing: boolean
    }

type DownloadFormat = 'zip' | 'pdf'

type Download =
  | {
      status: 'idle'
    }
  | {
      status: 'generatingZip'
    }
  | {
      status: 'generatingPdfWorkpaper'
      destination: WorkpaperDestination
    }
  | {
      status: 'downloadAdditionalDocuments'
      errors: DocumentGenerationError[]
    }
  | {
      status: 'downloadingAdditionalDocumentsZip'
      errors: DocumentGenerationError[]
    }

type LoadedState = Extract<STDownloadFolderModuleState, { status: 'loaded' }>

type DownloadPhaseState<Status extends Download['status']> = Omit<LoadedState, 'download'> & {
  download: Extract<Download, { status: Status }>
}
type GeneratingZipState = DownloadPhaseState<'generatingZip'>
type GeneratingWorkpaperState = DownloadPhaseState<'generatingPdfWorkpaper'>
type DownloadingAdditionalDocumentsState = DownloadPhaseState<'downloadingAdditionalDocumentsZip'>

const ZIP_EXPORT_FAILED = { type: 'zipExportFailed' } as const
const WORKPAPER_EXPORT_FAILED = { type: 'workpaperExportFailed' } as const
const ADDITIONAL_DOCUMENTS_EXPORT_FAILED = { type: 'additionalDocumentsExportFailed' } as const

const loadFolderDownloadState = Task.define((folderId: string) =>
  loadFolderDownloadStateEffect(folderId)
)

const exportZip = Task.define(exportZipEffect, { key: (state) => state.folderId })

const exportWorkpaper = Task.define(exportWorkpaperEffect, { key: (state) => state.folderId })

const exportAdditionalDocuments = Task.define(exportAdditionalDocumentsEffect, {
  key: (state) => state.folderId
})

export type STDownloadFolderMessage =
  | { type: 'opened'; folderId: string }
  | { type: 'closed' }
  | { type: 'retryLoad' }
  | { type: 'downloadZip' }
  | { type: 'downloadPdfWorkpaper'; destination: WorkpaperDestination }
  | { type: 'downloadAdditionalDocumentsZip' }
  | { type: 'toggleSelection'; documentId: string }
  | { type: 'toggleSelectAll' }
  | { type: 'toggleSort'; column: DownloadListSortColumn }
  | { type: 'closeDownloadAdditionalDocuments' }
  | TaskMessage<typeof loadFolderDownloadState>
  | TaskMessage<typeof exportZip>
  | TaskMessage<typeof exportWorkpaper>
  | TaskMessage<typeof exportAdditionalDocuments>

function isIgnoredFolderDownloadMessage({
  state,
  message
}: {
  state: STDownloadFolderModuleState
  message: STDownloadFolderMessage
}): boolean {
  return match(message)
    .with({ type: 'retryLoad' }, () => state.status != 'loadFailed')
    .with(
      { type: P.union('toggleSelection', 'toggleSelectAll', 'toggleSort') },
      () => state.status != 'loaded'
    )
    .with(
      { type: P.union('downloadZip', 'downloadPdfWorkpaper') },
      () => state.status != 'loaded' || state.download.status != 'idle'
    )
    .with(
      { type: 'downloadAdditionalDocumentsZip' },
      () => state.status != 'loaded' || state.download.status != 'downloadAdditionalDocuments'
    )
    .with(
      { type: 'closeDownloadAdditionalDocuments' },
      () =>
        state.status != 'loaded' ||
        (state.download.status != 'downloadAdditionalDocuments' &&
          state.download.status != 'downloadingAdditionalDocumentsZip')
    )
    .otherwise(() => false)
}

export const stDownloadFolderModule = defModule<{ state: STDownloadFolderModuleState; message: STDownloadFolderMessage }>({
  init: () => ({ status: 'idle' }),
  tasks: folderDownloadTasks,
  update: pipe(
    updateFolderDownload,
    Update.ignoreWhen<STDownloadFolderModuleState, STDownloadFolderMessage>(
      isIgnoredFolderDownloadMessage
    )
  )
})

function updateFolderDownload(
  state: STDownloadFolderModuleState,
  message: STDownloadFolderMessage
): STDownloadFolderModuleState {
  return match({ state, message })
    .returnType<STDownloadFolderModuleState>()
    .with({ message: { type: 'opened' } }, ({ message }) => ({
      status: 'loading',
      folderId: message.folderId
    }))
    .with({ message: { type: 'closed' } }, () => ({ status: 'idle' }))
    .with({ state: { status: 'loadFailed' }, message: { type: 'retryLoad' } }, ({ state }) => ({
      status: 'loading',
      folderId: state.folderId
    }))
    .with(
      { state: { status: 'loading' }, message: { type: 'downloadStateLoaded' } },
      ({ state, message }) => loadedFolderDownload(state.folderId, message.downloadState)
    )
    .with(
      {
        state: { status: 'loaded', refreshing: true },
        message: { type: 'downloadStateLoaded' }
      },
      ({ state, message }) => ({
        ...state,
        downloadState: message.downloadState,
        refreshing: false
      })
    )
    .with(
      { state: { status: 'loading' }, message: { type: 'downloadStateLoadFailed' } },
      ({ state }) => ({ status: 'loadFailed', folderId: state.folderId })
    )
    .with(
      {
        state: { status: 'loaded', refreshing: true },
        message: { type: 'downloadStateLoadFailed' }
      },
      ({ state }) => ({ ...state, refreshing: false })
    )
    .with(
      { state: { status: 'loaded' }, message: { type: 'toggleSelection' } },
      ({ state, message }) => ({
        ...state,
        selectedDocumentIds: setToggle(state.selectedDocumentIds, message.documentId)
      })
    )
    .with({ state: { status: 'loaded' }, message: { type: 'toggleSelectAll' } }, ({ state }) => ({
      ...state,
      selectedDocumentIds: toggledDocumentSelection(state)
    }))
    .with(
      { state: { status: 'loaded' }, message: { type: 'toggleSort' } },
      ({ state, message }) => ({
        ...state,
        sortState: toggleSort(state.sortState, message.column, SORT_TRANSITIONS)
      })
    )
    .with(
      {
        state: { status: 'loaded', download: { status: 'idle' } },
        message: { type: 'downloadZip' }
      },
      ({ state }) => ({ ...state, download: { status: 'generatingZip' } })
    )
    .with(
      {
        state: { status: 'loaded', download: { status: 'idle' } },
        message: { type: 'downloadPdfWorkpaper' }
      },
      ({ state, message }) => ({
        ...state,
        download: { status: 'generatingPdfWorkpaper', destination: message.destination }
      })
    )
    .with(
      {
        state: {
          status: 'loaded',
          download: { status: 'downloadAdditionalDocuments' }
        },
        message: { type: 'downloadAdditionalDocumentsZip' }
      },
      ({ state }) => ({
        ...state,
        download: { ...state.download, status: 'downloadingAdditionalDocumentsZip' }
      })
    )
    .with(
      P.union(
        {
          state: { status: 'loaded', download: { status: 'generatingZip' } },
          message: { type: 'zipExported' }
        },
        {
          state: {
            status: 'loaded',
            download: { status: 'downloadingAdditionalDocumentsZip' }
          },
          message: { type: 'additionalDocumentsExported' }
        }
      ),
      ({ state }) => ({ ...state, download: { status: 'idle' }, refreshing: true })
    )
    .with(
      {
        state: { status: 'loaded', download: { status: 'generatingPdfWorkpaper' } },
        message: { type: 'workpaperExported' }
      },
      ({ state, message }) => ({
        ...state,
        download: workpaperCompletion(message.errors),
        refreshing: true
      })
    )
    .with(
      P.union(
        {
          state: { status: 'loaded', download: { status: 'generatingZip' } },
          message: { type: 'zipExportFailed' }
        },
        {
          state: { status: 'loaded', download: { status: 'generatingPdfWorkpaper' } },
          message: { type: 'workpaperExportFailed' }
        }
      ),
      ({ state }) => ({ ...state, download: { status: 'idle' } })
    )
    .with(
      {
        state: {
          status: 'loaded',
          download: { status: 'downloadingAdditionalDocumentsZip' }
        },
        message: { type: 'additionalDocumentsExportFailed' }
      },
      ({ state }) => ({
        ...state,
        download: { ...state.download, status: 'downloadAdditionalDocuments' }
      })
    )
    .with(
      {
        state: {
          status: 'loaded',
          download: {
            status: P.union('downloadAdditionalDocuments', 'downloadingAdditionalDocumentsZip')
          }
        },
        message: { type: 'closeDownloadAdditionalDocuments' }
      },
      ({ state }) => ({ ...state, download: { status: 'idle' } })
    )
    .otherwise(() => {
      throw unexpectedMessage(state, message)
    })
}

function unexpectedMessage(state: STDownloadFolderModuleState, message: STDownloadFolderMessage) {
  return new Error(`Unexpected folder download message ${message.type} while ${state.status}`)
}

/**
 * A loaded dialog has two independent pieces of work: refreshing its server
 * snapshot and exporting the current selection. Each Task is present only
 * during the state interval that owns its output, so close or replacement
 * revokes the work without reducer-level request correlation.
 */
function folderDownloadTasks(
  state: STDownloadFolderModuleState
): TaskDeclaration<STDownloadFolderMessage>[] {
  const tasks: TaskDeclaration<STDownloadFolderMessage>[] = []

  match(state)
    .with({ status: 'loading' }, ({ folderId }) => tasks.push(loadFolderDownloadState(folderId)))
    .with({ status: 'loaded', refreshing: true }, ({ folderId }) =>
      tasks.push(loadFolderDownloadState(folderId))
    )
    .with(
      P.union({ status: 'idle' },
      { status: 'loadFailed' },
      { status: 'loaded', refreshing: false }),
      () => undefined
    )
    .exhaustive()

  match(state)
    .with({ status: P.union('idle', 'loading', 'loadFailed') }, () => undefined)
    .with({ status: 'loaded', download: { status: 'idle' } }, () => undefined)
    .with({ status: 'loaded', download: { status: 'generatingZip' } }, (state) =>
      tasks.push(exportZip(state))
    )
    .with({ status: 'loaded', download: { status: 'generatingPdfWorkpaper' } }, (state) =>
      tasks.push(exportWorkpaper(state))
    )
    .with(
      {
        status: 'loaded',
        download: { status: 'downloadAdditionalDocuments' }
      },
      () => undefined
    )
    .with(
      {
        status: 'loaded',
        download: { status: 'downloadingAdditionalDocumentsZip' }
      },
      (state) => tasks.push(exportAdditionalDocuments(state))
    )
    .exhaustive()

  return tasks
}

export function openDownloadFolderDialog(args: { folderId: string }): STDownloadFolderMessage {
  return { type: 'opened', folderId: args.folderId }
}

export function closeDownloadFolderDialog(): STDownloadFolderMessage {
  return { type: 'closed' }
}

function loadFolderDownloadStateEffect(folderId: string) {
  return Effect.gen(function* () {
    const sdk = yield* SdkClient
    const result = yield* runSdkRequest(
      sdk.send({ type: 'folders/getFolderDownloadState', folderId })
    )

    if (result.ok) {
      return {
        type: 'downloadStateLoaded',
        downloadState: result.response
      } as const
    }

    yield* reportHandledError(result.error, {
      source: 'st-download-folder',
      operation: 'load-download-state'
    })
    return { type: 'downloadStateLoadFailed' } as const
  })
}

function loadedFolderDownload(folderId: string, downloadState: STFolderDownloadState): LoadedState {
  return {
    status: 'loaded',
    folderId,
    downloadState,
    selectedDocumentIds: defaultSelectedDocumentIds(downloadState),
    download: { status: 'idle' },
    sortState: undefined,
    refreshing: false
  }
}

function defaultSelectedDocumentIds(downloadState: STFolderDownloadState): string[] {
  return downloadState.documents
    .map((document) => document.id)
    .filter((documentId) => {
      const warnings = downloadState.documentWarnings[documentId]?.warnings ?? []
      return !warnings.some((warning) => warning.type == 'duplicate')
    })
}

function toggledDocumentSelection(state: LoadedState): string[] {
  if (selAllDocumentsSelected(state)) return []
  return state.downloadState.documents.map((document) => document.id)
}

function workpaperCompletion(errors: DocumentGenerationError[]): Download {
  if (errors.length == 0) return { status: 'idle' }
  return { status: 'downloadAdditionalDocuments', errors }
}

function downloadFailure<Message>(format: DownloadFormat, error: unknown, message: Message) {
  return Effect.gen(function* () {
    yield* reportHandledError(error, {
      source: 'st-download-folder',
      operation: `download-${format}`
    })
    const snackbar = yield* Snackbar
    yield* snackbar.show(
      'Download failed. Please try again and contact us if the problem persists.',
      { variant: 'error' }
    )
    return message
  })
}

function exportZipEffect(state: GeneratingZipState) {
  return exportDocumentsAsZipEffect(state, state.selectedDocumentIds).pipe(
    Effect.as({ type: 'zipExported' } as const),
    recoverZipExport(ZIP_EXPORT_FAILED)
  )
}

function exportDocumentsAsZipEffect(state: LoadedState, documentIds: string[]) {
  return Effect.gen(function* () {
    const sdk = yield* SdkClient
    const downloads = yield* BrowserDownloads
    const entries = yield* generateZipDownloadDirectoryEffect(state.downloadState, documentIds)

    const zipFilename = formatFolderEntityName(state.downloadState.folder.entities) + '.zip'
    if (entries.length > 0) {
      const zipFile = yield* zipEntries(entries, zipFilename)
      yield* downloads.save(zipFile)
    }

    yield* sdk.send({
      type: 'folders/markFolderDocumentsExported',
      folderId: state.folderId,
      documentIds,
      exportFormat: 'zip'
    })
  })
}

function exportWorkpaperEffect(state: GeneratingWorkpaperState) {
  return Effect.gen(function* () {
    const sdk = yield* SdkClient
    const downloads = yield* BrowserDownloads
    const result = yield* runSdkRequest(
      sdk.send({
        type: 'folders/generateWorkpaper',
        folderId: state.folderId,
        documentIds: state.selectedDocumentIds,
        includeAnnotations: true,
        destination: state.download.destination
      })
    )
    if (!result.ok) {
      return yield* downloadFailure('pdf', result.error, WORKPAPER_EXPORT_FAILED)
    }

    if (state.download.destination == 'download') {
      yield* downloads.save(result.response.downloadUrl, result.response.filename)
    }

    return { type: 'workpaperExported', errors: result.response.errors } as const
  })
}

function exportAdditionalDocumentsEffect(state: DownloadingAdditionalDocumentsState) {
  const documentIds = state.download.errors.map((error) => error.documentId)
  return exportDocumentsAsZipEffect(state, documentIds).pipe(
    Effect.as({ type: 'additionalDocumentsExported' } as const),
    recoverZipExport(ADDITIONAL_DOCUMENTS_EXPORT_FAILED)
  )
}

type ZipExportFailure =
  | SdkFailure
  | BrowserDownloadFetchError
  | FileReadError
  | ArchiveGenerationError

function recoverZipExport<Message>(failureMessage: Message) {
  return <A, R>(effect: Effect.Effect<A, ZipExportFailure, R>) =>
    effect.pipe(
      Effect.catchTags({
        TransportError: (error) => downloadFailure('zip', error, failureMessage),
        TimeoutError: (error) => downloadFailure('zip', error, failureMessage),
        HttpStatusError: (error) => downloadFailure('zip', error, failureMessage),
        BrowserDownloadFetchError: (error) => downloadFailure('zip', error.cause, failureMessage),
        FileReadError: (error) => downloadFailure('zip', error.cause, failureMessage),
        ArchiveGenerationError: (error) => downloadFailure('zip', error.cause, failureMessage)
      })
    )
}

function generateZipDownloadDirectoryEffect(
  downloadState: STFolderDownloadState,
  documentIds: string[]
) {
  const selectedIds = new Set(documentIds)
  const sections = getDocumentSections({
    documents: downloadState.documents.filter((doc) => selectedIds.has(doc.id)),
    documentTypes: downloadState.documentTypes
  })

  const usedPaths = new Set<string>()
  return Effect.forEach(
    sections.flatMap((section) => section.items),
    (doc) =>
      Effect.gen(function* () {
        const sdk = yield* SdkClient
        const downloads = yield* BrowserDownloads
        const result = yield* sdk.send({
          type: 'folders/prepareDocumentDownload',
          folderId: downloadState.folder.id,
          documentId: doc.id
        })
        const filename = uniqueZipPath(sanitizeFilename(result.filename), usedPaths)
        const blob = yield* downloads.fetchBlob(result.downloadUrl)
        const lastModified = parseISO8601Time(doc.uploadedAt!).getTime()
        const fileObject = new File([blob], filename, {
          lastModified,
          type: blob.type
        })
        return browserFileEntry(filename, fileObject)
      }),
    { concurrency: 10 }
  )
}

function uniqueZipPath(path: string, used: Set<string>) {
  if (!used.has(path)) {
    used.add(path)
    return path
  }

  const base = rootname(path)
  const extension = extname(path)
  let i = 1
  while (used.has(`${base} (${i})${extension}`)) {
    i++
  }
  const next = `${base} (${i})${extension}`
  used.add(next)
  return next
}

export type DocumentItem = {
  document: STDocument
  documentType: STDocumentType
  warnings: DocumentWarning[]
}

export function selDownloadableDocumentList(state: STDownloadFolderModuleState): DocumentItem[] {
  if (state.status != 'loaded') return []
  const warningsByDocumentId = state.downloadState.documentWarnings

  // we start out with sorting that matches the left bookmark sidebar
  const sections = getDocumentSections({
    documentTypes: state.downloadState.documentTypes,
    documents: state.downloadState.documents
  })

  const items = sections.flatMap((s) =>
    s.items.map((d) => ({
      document: d,
      documentType: s.heading,
      warnings: warningsByDocumentId[d.id]?.warnings ?? []
    }))
  )

  switch (state.sortState?.column) {
    case 'name':
      return sort(
        items,
        direction(state.sortState.direction, (i) => i.document.name.toLowerCase())
      )
    case 'uploadedAt':
      return sort(
        items,
        direction(state.sortState.direction, (i) => i.document.uploadedAt ?? '')
      )
    case 'exportedAt':
      return sort(
        items,
        direction(state.sortState.direction, (i) => i.document.exportedAt ?? '')
      )
    default:
      return items
  }
}

export function selAllDocumentsSelected(state: STDownloadFolderModuleState) {
  if (state.status != 'loaded') return false
  return state.selectedDocumentIds.length == state.downloadState.documents.length
}

type AdditionalDocument = {
  document: STDocument
  documentType: STDocumentType
  error: DocumentGenerationError
}

export function selDownloadAdditionalDocuments(
  state: STDownloadFolderModuleState
): AdditionalDocument[] {
  if (state.status != 'loaded') return []
  const download = state.download
  if (
    download.status != 'downloadAdditionalDocuments' &&
    download.status != 'downloadingAdditionalDocumentsZip'
  ) {
    return []
  }

  return selDownloadableDocumentList(state)
    .map((d) => {
      const error = download.errors.find((e) => e.documentId == d.document.id)
      if (!error) {
        return undefined
      }
      return {
        document: d.document,
        documentType: d.documentType,
        error: error
      } as AdditionalDocument
    })
    .filter(isNotEmpty)
}

export type DownloadListSortColumn = 'name' | 'uploadedAt' | 'exportedAt'

const SORT_TRANSITIONS: SortTransitions<DownloadListSortColumn> = {
  name: ['asc', 'desc', undefined],
  uploadedAt: ['desc', 'asc', undefined],
  exportedAt: ['desc', 'asc', undefined]
}
